import React from 'react';
import { Link } from 'react-router-dom';
import {
    Scissors,
    MessageSquare,
    HelpCircle,
    BarChart2,
    Download,
    SplitSquareHorizontal,
    Music,
    Brain
} from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { DotScreenShader } from '../components/DotScreenShader';
import { BottomCTA } from '../components/BottomCTA';
import './FeaturesPage.css';

const TOOL_CATEGORIES = [
    {
        id: 'smart-clips',
        title: 'Smart Clips',
        subtitle: 'AI-powered long-form to short-form video conversion. Uses MediaPipe Face Detection for intelligent subject tracking, OpenAI Whisper for word-level transcription, and a custom Viral Scoring algorithm that analyzes linguistic hooks, audio energy, and trend relevance to identify the most engaging moments.',
        icon: <Scissors size={32} />,
        featured: true,
        aiFeatures: [
            { name: 'Smart Cropping', tech: 'MediaPipe BlazeFace', description: 'Tracks faces and automatically centers the frame on the speaker using weighted attention algorithms.' },
            { name: 'Viral Scoring', tech: 'Custom ML Formula', description: 'Analyzes hook phrases, audio intensity, speech rate, and trend keywords to predict clip performance.' },
            { name: 'Transcription', tech: 'OpenAI Whisper / Deepgram', description: 'Word-level timestamps for perfectly synced animated captions.' },
        ]
    },
    {
        id: 'rants',
        title: 'Rants',
        subtitle: 'AI script generation with GPT models creates chaotic, hyper-relatable monologues. ElevenLabs Turbo v2.5 generates fast-paced voiceovers with word-level timestamps for dynamic caption sync.',
        icon: <MessageSquare size={32} />,
        featured: true,
        aiFeatures: [
            { name: 'Script Generation', tech: 'GPT', description: 'Creates 100-150 word viral scripts with hooks, escalation, and punchlines.' },
            { name: 'Voice Synthesis', tech: 'ElevenLabs', description: 'Hyper-realistic AI voices with word-level alignment.' },
            { name: 'Topic Ideation', tech: 'GPT', description: 'Generates unique, niche micro-anxiety topics that avoid common tropes.' },
        ]
    },
    {
        id: 'quizzes',
        title: 'Quizzes',
        subtitle: 'AI-generated trivia content that creates engaging questions with varying difficulty, plausible distractor answers, and randomized correct answer positions to prevent pattern guessing.',
        icon: <HelpCircle size={32} />,
        featured: true,
        aiFeatures: [
            { name: 'Question Generation', tech: 'GPT', description: 'Creates topic-specific questions with 3 answer choices and varied difficulty.' },
            { name: 'Topic Discovery', tech: 'GPT', description: 'Suggests niche, engaging topics beyond generic trivia.' },
            { name: 'Answer Shuffling', tech: 'Smart Logic', description: 'Randomizes correct answer positions for A, B, C distribution.' },
        ]
    },
    {
        id: 'ranked',
        title: 'Ranked',
        subtitle: 'Automated countdown/top-list video creation with dynamic overlays, numbering, and professional transitions without manual editing.',
        icon: <BarChart2 size={32} />,
        aiFeatures: [
            { name: 'Smart Templating', tech: 'Video Engine', description: 'Dynamic text overlays, scaling, and rank number generation.' },
            { name: 'Transitions', tech: 'Pro Effects', description: 'Professional slide, fade, and dissolve transitions between clips.' },
        ]
    },
    {
        id: 'downloader',
        title: 'Video & Audio Downloader',
        subtitle: 'Universal downloader supporting 1800+ platforms including YouTube, TikTok, Instagram, Twitter, and more.',
        icon: <Download size={32} />,
        aiFeatures: [
            { name: 'Platform Detection', tech: 'Universal', description: 'Automatically detects platform and selects optimal download format.' },
        ]
    },
    {
        id: 'video-splitter',
        title: 'Video Splitter',
        subtitle: 'Precision video splitting into platform-optimized chunks (30s or 60s) with lossless cutting and no re-encoding.',
        icon: <SplitSquareHorizontal size={32} />,
        aiFeatures: [
            { name: 'Lossless Splitting', tech: 'Video Engine', description: 'Fast stream-copy cutting without quality loss.' },
        ]
    },
    {
        id: 'audio-converter',
        title: 'Audio Converter',
        subtitle: 'Convert video or audio files to MP3/WAV formats. Extracts audio tracks for podcast clips, music, or voiceovers.',
        icon: <Music size={32} />,
        aiFeatures: [
            { name: 'Format Conversion', tech: 'Audio Engine', description: 'High-quality audio extraction and format conversion.' },
        ]
    },
];

export const FeaturesPage = () => {
    return (
        <div className="App" style={{ position: 'relative' }}>
            <div style={{ position: 'fixed', inset: 0, zIndex: 0 }}>
                <DotScreenShader theme="dark" />
            </div>

            <Navbar />

            <main className="hero" style={{ position: 'relative', zIndex: 1, paddingTop: '100px', minHeight: 'auto', paddingBottom: '20px' }}>
                <p className="hero-preheader hero-preheader--centered">AI-Powered Video Tools</p>
                <h1 className="hero-heading hero-heading--centered">Powerful Features for Content Creators</h1>
                <p className="hero-subheader">
                    Create viral short-form content with our suite of AI-powered video tools.
                    From intelligent clipping to voice synthesis, we've got you covered.
                </p>

                {/* Feature Sections - Vertical Layout */}
                <div className="features-list">
                    {TOOL_CATEGORIES.map((tool) => (
                        <section key={tool.id} id={tool.id} className="feature-section">
                            <div className="feature-header">
                                <div>
                                    <h2>{tool.title}</h2>
                                    <p className="feature-description">{tool.subtitle}</p>
                                </div>
                            </div>

                            {tool.aiFeatures && tool.aiFeatures.length > 0 && (
                                <div className="ai-features-grid">
                                    {tool.aiFeatures.map((feature, idx) => (
                                        <div key={idx} className="ai-feature-card">
                                            <div className="ai-feature-header">
                                                <Brain size={18} className="ai-icon" />
                                                <h3>{feature.name}</h3>
                                            </div>
                                            <div className="ai-tech-badge">{feature.tech}</div>
                                            <p>{feature.description}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    ))}
                </div>
            </main>

            {/* Bottom CTA Section */}
            <BottomCTA />

            <Footer />
        </div>
    );
};

export default FeaturesPage;
